import { normalize } from 'normalizr';

import { setQueryDataFromQuoteHeader } from '@frontend/api';

import { notify } from 'utils/reduxUtils';

import * as schema from '../schema';
import * as types from '../types';

export const initEditQuote = (quote) => ({
  type: types.INIT_EDIT_QUOTE,
  payload: setQueryDataFromQuoteHeader(quote) || quote,
});

export const receiveQuote = (quote) => ({
  type: types.QUOTE_RECEIVE,
  payload: setQueryDataFromQuoteHeader(quote) || quote,
});

export const clearQuoteFromStore = () => ({
  type: types.QUOTE_CLEAR,
});

export const cancelQuote = {
  request: () => ({
    type: types.QUOTE_CANCEL_REQUEST,
  }),
  success: () => ({
    type: types.QUOTE_CANCEL_SUCCESS,
  }),
  error: notify(
    ({ error }) => ({
      type: types.QUOTE_CANCEL_ERROR,
      payload: error,
    }),
    "Couldn't cancel quote",
  ),
};

export const confirmCancelQuote = () => ({
  type: types.CONFIRM_CANCEL_QUOTE,
});

export const closeCancelQuoteConfirmationModal = () => ({
  type: types.CLOSE_CANCEL_QUOTE_CONFIRMATION_MODAL,
});

export const markQuoteLost = {
  request: ({ onSettledCallback }) => ({
    type: types.QUOTE_MARK_LOST_REQUEST,
    payload: { onSettledCallback },
  }),
  success: () => ({
    type: types.QUOTE_MARK_LOST_SUCCESS,
  }),
  error: notify(
    ({ error }) => ({
      type: types.QUOTE_MARK_LOST_ERROR,
      payload: error,
    }),
    "Couldn't mark quote as lost",
  ),
};

export const markQuoteSent = {
  request: ({ onSettledCallback }) => ({
    type: types.QUOTE_MARK_SENT_REQUEST,
    payload: { onSettledCallback },
  }),
  success: notify(
    () => ({
      type: types.QUOTE_MARK_SENT_SUCCESS,
    }),
    'Quote marked as sent',
  ),
  error: notify(
    (error) => ({
      type: types.QUOTE_MARK_SENT_ERROR,
      payload: error,
    }),
    "Couldn't mark quote as sent",
  ),
};

export const createRevision = {
  request: ({ quoteId }) => ({
    type: types.QUOTE_CREATE_REVISION_REQUEST,
    payload: { quoteId },
  }),
  success: notify(
    () => ({
      type: types.QUOTE_CREATE_REVISION_SUCCESS,
    }),
    'Created quote revision',
  ),

  error: notify(
    (error) => ({
      type: types.QUOTE_CREATE_REVISION_ERROR,
      payload: error,
    }),
    "Couldn't create quote revision",
  ),
};

export const finalizeQuote = {
  flow: ({ resendQuote, quoteId }) => ({
    type: types.QUOTE_FINALIZE_FLOW,
    payload: { resendQuote, quoteId },
  }),
  request: () => ({
    type: types.QUOTE_FINALIZE_REQUEST,
  }),
  success: (token, shippingInfo) => ({
    type: types.QUOTE_FINALIZE_SUCCESS,
    payload: { token, shippingInfo },
  }),
  error: notify(
    ({ error }) => ({
      type: types.QUOTE_FINALIZE_ERROR,
      payload: error,
    }),
    "Couldn't finalize quote",
  ),
};

export const fetchEmailTemplates = {
  request: (payload) => ({
    type: types.FETCH_EMAIL_TEMPLATES,
    payload,
  }),
  success: ({ emailTemplates }) => ({
    type: types.FETCH_EMAIL_TEMPLATES_SUCCESS,
    payload: { emailTemplates },
  }),
  error: notify(
    ({ error }) => ({
      type: types.FETCH_EMAIL_TEMPLATES_ERROR,
      payload: error,
    }),
    "Couldn't get email templates",
  ),
};

export const sendQuote = {
  request: ({ emailData, isResend, quoteId }) => ({
    type: types.QUOTE_SEND_REQUEST,
    payload: { emailData, isResend, quoteId },
  }),
  success: notify(
    () => ({
      type: types.QUOTE_SEND_SUCCESS,
    }),
    'Sent quote',
  ),
  error: notify(
    ({ error }) => ({
      type: types.QUOTE_SEND_ERROR,
      payload: error,
    }),
    "Couldn't send quote",
  ),
};

export const changeTax = {
  request: ({ taxRate }) => ({
    type: types.CHANGE_TAX_REQUEST,
    payload: { taxRate },
  }),
  success: () => ({
    type: types.CHANGE_TAX_SUCCESS,
  }),
  error: notify(
    (error, revision) => ({
      type: types.CHANGE_TAX_ERROR,
      payload: error,
      meta: { revision },
    }),
    "Couldn't apply tax",
  ),
};

export const changeExpiredDate = ({ expiredDate }) => ({
  type: types.CHANGE_EXPIRED_DATE,
  payload: { expiredDate },
});

export const changeDueDate = {
  error: notify(
    (error) => ({
      type: types.CHANGE_DUE_DATE_ERROR,
      payload: error,
    }),
    "Couldn't change due date",
  ),
  request: ({ dueDate }) => ({
    type: types.CHANGE_DUE_DATE_REQUEST,
    payload: { dueDate },
  }),
};

export const changePriority = {
  request: ({ priority }) => ({
    type: types.CHANGE_QUOTE_PRIORITY_REQUEST,
    payload: { priority },
  }),
  success: () => ({ type: types.CHANGE_QUOTE_PRIORITY_SUCCESS }),
  error: notify(
    (error) => ({
      type: types.CHANGE_QUOTE_PRIORITY_ERROR,
      payload: error,
    }),
    "Couldn't change quote priority",
  ),
};

export const changeRequestedDate = {
  error: notify(
    (error) => ({
      type: types.CHANGE_REQUESTED_DATE_ERROR,
      payload: error,
    }),
    "Couldn't change requested date",
  ),
  request: ({ requestedDate }) => ({
    type: types.CHANGE_REQUESTED_DATE_REQUEST,
    payload: { requestedDate },
  }),
};

export const updateCustomerFacingNotes = ({ notes }) => ({
  type: types.CUSTOMER_FACING_NOTES_UPDATE,
  payload: { notes },
});

export const updateQuotesPrivateNotes = {
  request: ({ notes, toast }) => ({
    type: types.QUOTES_PRIVATE_NOTES_UPDATE,
    payload: { notes, toast },
  }),
  success: ({ toast = false }) => {
    const data = { type: types.QUOTES_PRIVATE_NOTES_UPDATE_SUCCESS };
    if (toast) {
      return notify(() => data, 'Updated private notes')();
    } else {
      return data;
    }
  },
  error: notify(
    (error) => ({
      type: types.QUOTES_PRIVATE_NOTES_UPDATE_ERROR,
      payload: error,
    }),
    "Couldn't update private notes",
  ),
};

export const upload = ({
  file,
  groupId = undefined,
  units = undefined,
  position = undefined,
}) => ({
  type: types.UPLOAD_REQUEST,
  payload: { file, groupId, units, position },
});

export const uploadSuccess = () => ({
  type: types.UPLOAD_SUCCESS,
});

export const uploadError = notify(
  ({ error }) => ({
    type: types.UPLOAD_ERROR,
    payload: error,
  }),
  "Couldn't upload part",
  '',
  null, // set toast notification to remain open - acknowledge when file size is too large or upload fails
);

export const reinterrogate = {
  request: (payload) => ({
    type: types.PART_REINTERROGATION_REQUEST,
    payload,
  }),
  success: (payload) => ({
    type: types.PART_REINTERROGATION_SUCCESS,
    payload,
  }),
  error: (payload) => ({
    type: types.PART_REINTERROGATION_ERROR,
    payload,
  }),
};

export const refreshQuote = {
  request: (payload) => ({
    type: types.QUOTE_REFRESH_REQUEST,
    payload,
  }),
  success: (payload) => ({
    type: types.QUOTE_REFRESH_SUCCESS,
    payload,
  }),
  error: notify(
    (payload) => ({
      type: types.QUOTE_REFRESH_ERROR,
      payload,
    }),
    "Couldn't refresh quote",
  ),
};

export const convertQuoteFilesToLineItems = {
  request: ({ fileUuids }) => ({
    type: types.CONVERT_QUOTE_FILES_TO_LINE_ITEMS_REQUEST,
    payload: { fileUuids },
  }),
  finish: ({ fileUuids }) => ({
    type: types.CONVERT_QUOTE_FILES_TO_LINE_ITEMS_FINISH,
    payload: { fileUuids },
  }),
};

export const receiveOpenTaskCount = ({ openTaskCount }) => ({
  type: types.RECEIVE_OPEN_TASK_COUNT,
  payload: { openTaskCount },
});

const normalizeComponentAndQuantity = ({ component, quoteItemQuantity }) => {
  const entities = {};
  if (component) {
    Object.assign(
      entities,
      normalize(component, schema.quoteItemComponent).entities,
    );
  }
  if (quoteItemQuantity) {
    Object.assign(
      entities,
      normalize(quoteItemQuantity, schema.quoteItemQuantity).entities,
    );
  }
  return { entities };
};

export const receiveQuantities = (quantities) => ({
  type: types.RECEIVE_QUANTITIES,
  payload: normalize(quantities, schema.quoteItemQuantities),
});

export const updateQuantity = {
  request: (payload) => ({
    type: types.QUANTITY_UPDATE_REQUEST,
    payload,
  }),
  success: ({ component, quoteItemQuantity }) => ({
    type: types.QUANTITY_UPDATE_SUCCESS,
    payload: normalizeComponentAndQuantity({ component, quoteItemQuantity }),
  }),
  commit: ({ component, quoteItemQuantity }) => ({
    type: types.QUANTITY_UPDATE_COMMIT,
    payload: normalizeComponentAndQuantity({ component, quoteItemQuantity }),
  }),
  error: notify(
    ({ error }) => ({
      type: types.QUANTITY_UPDATE_ERROR,
      payload: error,
    }),
    "Couldn't update quote item quantity",
  ),
};

export const updateExpedites = {
  request: (payload) => ({
    type: types.EXPEDITES_UPDATE_REQUEST,
    payload,
  }),
  success: ({ quoteItemQuantity }) => ({
    type: types.EXPEDITES_UPDATE_SUCCESS,
    payload: normalize(quoteItemQuantity, schema.quoteItemQuantity),
  }),
  error: notify(
    ({ error }) => ({
      type: types.EXPEDITES_UPDATE_ERROR,
      payload: error,
    }),
    "Couldn't update expedites",
  ),
};

export const updateFeatures = {
  request: (payload) => ({
    type: types.FEATURES_UPDATE_REQUEST,
    payload,
  }),
  success: ({ quoteItemComponent }) => ({
    type: types.FEATURES_UPDATE_SUCCESS,
    payload: normalize(quoteItemComponent, schema.quoteItemComponent),
  }),
  error: notify(
    ({ error }) => ({
      type: types.FEATURES_UPDATE_ERROR,
      payload: error,
    }),
    "Couldn't update features",
  ),
};

export const updateOverrides = {
  request: (payload) => ({
    type: types.OVERRIDES_UPDATE_REQUEST,
    payload,
  }),
  success: ({ quoteItemComponent }) => ({
    type: types.OVERRIDES_UPDATE_SUCCESS,
    payload: normalize(quoteItemComponent, schema.quoteItemComponent),
  }),
  commit: ({ quoteItemComponent }) => ({
    type: types.OVERRIDES_UPDATE_COMMIT,
    payload: normalize(quoteItemComponent, schema.quoteItemComponent),
  }),
  error: notify(
    ({ error }) => ({
      type: types.OVERRIDES_UPDATE_ERROR,
      payload: error,
    }),
    "Couldn't update overrides",
  ),
};

export const createQuoteCSV = {
  request: () => ({
    type: types.QUOTE_CREATE_CSV_REQUEST,
  }),
  success: () => ({
    type: types.QUOTE_CREATE_CSV_SUCCESS,
  }),
  error: notify(
    () => ({
      type: types.QUOTE_CREATE_CSV_ERROR,
    }),
    "Couldn't create CSV",
  ),
};

export const selectContact = {
  error: notify(
    () => ({
      type: types.CONTACT_SELECT_ERROR,
    }),
    "Couldn't select contact",
  ),
  request: ({ contactId }) => ({
    type: types.CONTACT_SELECT_REQUEST,
    payload: { contactId },
  }),
  success: () => ({
    type: types.CONTACT_SELECT_SUCCESS,
  }),
};

export const selectEstimator = {
  error: notify(
    () => ({
      type: types.ESTIMATOR_SELECT_ERROR,
    }),
    "Couldn't select estimator",
  ),
  request: ({ estimator }) => ({
    type: types.ESTIMATOR_SELECT_REQUEST,
    payload: { estimator },
  }),
  success: () => ({
    type: types.ESTIMATOR_SELECT_SUCCESS,
  }),
};

export const selectSalesperson = {
  error: notify(
    () => ({
      type: types.SALESPERSON_SELECT_ERROR,
    }),
    "Couldn't select salesperson",
  ),
  request: ({ salesperson }) => ({
    type: types.SALESPERSON_SELECT_REQUEST,
    payload: { salesperson },
  }),
  success: () => ({
    type: types.SALESPERSON_SELECT_SUCCESS,
  }),
};

export const selectSupplierFacility = {
  error: notify(
    () => ({
      type: types.SUPPLIER_FACILITY_SELECT_ERROR,
    }),
    "Couldn't select supplier facility",
  ),
  request: ({ supplierFacilityId }) => ({
    type: types.SUPPLIER_FACILITY_SELECT_REQUEST,
    payload: { supplierFacilityId },
  }),
  success: () => ({
    type: types.SUPPLIER_FACILITY_SELECT_SUCCESS,
  }),
};

export const updatePrivateNotes = {
  request: (payload) => ({
    type: types.PRIVATE_NOTES_UPDATE_REQUEST,
    payload,
  }),
  success: ({ quoteItem }) => ({
    type: types.PRIVATE_NOTES_UPDATE_SUCCESS,
    payload: normalize(quoteItem, schema.quoteItem),
  }),
  error: notify(
    ({ error }) => ({
      type: types.PRIVATE_NOTES_UPDATE_ERROR,
      payload: error,
    }),
    "Couldn't update notes",
  ),
};

export const updatePublicNotes = {
  request: (payload) => ({
    type: types.PUBLIC_NOTES_UPDATE_REQUEST,
    payload,
  }),
  success: ({ quoteItem }) => ({
    type: types.PUBLIC_NOTES_UPDATE_SUCCESS,
    payload: normalize(quoteItem, schema.quoteItem),
  }),
  error: notify(
    ({ error }) => ({
      type: types.PUBLIC_NOTES_UPDATE_ERROR,
      payload: error,
    }),
    "Couldn't update notes",
  ),
};

export const saveQuoteSettingsById = ({
  quoteWithSettingsId,
  quoteSettings,
}) => ({
  type: types.SAVE_QUOTE_SETTINGS_BY_ID,
  payload: { quoteWithSettingsId, quoteSettings },
});

export const copyExpediteOptionsRow = notify(
  (payload) => ({
    type: types.COPY_EXPEDITE_OPTIONS_ROW,
    payload,
  }),
  'Copied expedite options row to clipboard',
);

export const copyAllExpediteOptions = notify(
  (payload) => ({
    type: types.COPY_ALL_EXPEDITE_OPTIONS,
    payload,
  }),
  'Copied all expedite options to clipboard',
);

export const pasteExpediteOptionsRow = (payload) => ({
  type: types.PASTE_EXPEDITE_OPTIONS_ROW,
  payload,
});

export const pasteAllExpeditesOptions = (payload) => ({
  type: types.PASTE_ALL_EXPEDITE_OPTIONS,
  payload,
});

export const clearExpediteOptionsRow = (payload) => ({
  type: types.CLEAR_EXPEDITE_OPTIONS_ROW,
  payload,
});

export const clearAllExpeditesOptions = (payload) => ({
  type: types.CLEAR_ALL_EXPEDITE_OPTIONS,
  payload,
});

export const applyExpediteOptionsRowToAll = (payload) => ({
  type: types.APPLY_EXPEDITE_OPTIONS_ROW_TO_ALL,
  payload,
});

export const finishUpdatingExpediteOptions = () => ({
  type: types.FINISH_UPDATING_EXPEDITE_OPTIONS,
});

export const applyQuoteExpediteOptions = {
  request: ({ expedites, standardLeadTime }) => ({
    type: types.APPLY_QUOTE_EXPEDITE_OPTIONS_REQUEST,
    payload: { expedites, standardLeadTime },
  }),
  success: notify(
    () => ({
      type: types.APPLY_QUOTE_EXPEDITE_OPTIONS_SUCCESS,
    }),
    'Applied expedite options',
  ),
  error: notify(
    ({ error }) => ({
      type: types.APPLY_QUOTE_EXPEDITE_OPTIONS_ERROR,
      payload: error,
    }),
    "Couldn't apply expedite options",
  ),
};

export const clearQuoteExpediteOptions = {
  request: () => ({
    type: types.CLEAR_QUOTE_EXPEDITE_OPTIONS_REQUEST,
  }),
  success: notify(
    () => ({
      type: types.CLEAR_QUOTE_EXPEDITE_OPTIONS_SUCCESS,
    }),
    'Cleared expedite options',
  ),
  error: notify(
    ({ error }) => ({
      type: types.CLEAR_QUOTE_EXPEDITE_OPTIONS_ERROR,
      payload: error,
    }),
    "Couldn't clear expedite options",
  ),
};

export const contactConflict = {
  openModal: () => ({
    type: types.CONTACT_CONFLICT_OPEN_MODAL,
  }),
  closeModal: () => ({
    type: types.CONTACT_CONFLICT_CLOSE_MODAL,
  }),
  /* triggers saga */
  resolve: (buttonClicked) => ({
    type: types.CONTACT_CONFLICT_RESOLVE,
    payload: { buttonClicked },
  }),
  success: () => ({
    type: types.CONTACT_CONFLICT_SUCCESS,
  }),
  error: notify((error) => ({
    type: types.CONTACT_CONFLICT_ERROR,
    payload: error,
  })),
};

export const reorderQuoteItems = {
  flow: (position) => ({
    type: types.REORDER_QUOTE_ITEMS_FLOW,
    payload: { position },
  }),
  request: (position, nextPosition) => ({
    type: types.REORDER_QUOTE_ITEMS_REQUEST,
    payload: { position, nextPosition },
  }),
  success: (quoteItems) => ({
    type: types.REORDER_QUOTE_ITEMS_SUCCESS,
    payload: normalize(quoteItems, [schema.quoteItem]),
  }),
};

export const downloadNestPDF = {
  request: (quoteId, nestId) => ({
    type: types.DOWNLOAD_NEST_PDF_REQUEST,
    payload: { quoteId, nestId },
  }),
  success: () => ({
    type: types.DOWNLOAD_NEST_PDF_SUCCESS,
  }),
  error: (error) => ({
    type: types.DOWNLOAD_NEST_PDF_ERROR,
    payload: { error },
  }),
};

export const baqPageInitialization = {
  request: ({ revisionNumber, quoteNumber }) => ({
    type: types.BAQ_PAGE_INIT_REQUEST,
    payload: { revisionNumber, quoteNumber },
  }),
  success: () => ({
    type: types.BAQ_PAGE_INIT_SUCCESS,
  }),
  error: () => ({
    type: types.BAQ_PAGE_INIT_ERROR,
  }),
};

export const updateQuoteRfqNumber = {
  request: ({ rfqNumber }) => ({
    type: types.QUOTE_RFQ_NUMBER_UPDATE_REQUEST,
    payload: { rfqNumber },
  }),
  success: () => ({
    type: types.QUOTE_RFQ_NUMBER_UPDATE_SUCCESS,
  }),
  error: notify(
    (error) => ({
      type: types.QUOTE_RFQ_NUMBER_UPDATE_ERROR,
      payload: error,
    }),
    "Couldn't update quote RFQ number",
  ),
};

export const updateMetadata = {
  request: ({ metadata }) => ({
    type: types.QUOTE_METADATA_UPDATE_REQUEST,
    payload: { metadata },
  }),
  success: notify(
    () => ({
      type: types.QUOTE_METADATA_UPDATE_SUCCESS,
    }),
    'Updated quote metadata',
  ),
  error: notify(
    (error) => ({
      type: types.QUOTE_METADATA_UPDATE_ERROR,
      payload: error,
    }),
    "Couldn't update quote metadata",
  ),
};

export const exportQuoteSummary = {
  request: ({ quoteId }) => ({
    type: types.EXPORT_TO_EXCEL_REQUEST,
    payload: { quoteId },
  }),
  success: () => ({
    type: types.EXPORT_TO_EXCEL_SUCCESS,
  }),
  error: notify(
    (error) => ({
      type: types.EXPORT_TO_EXCEL_ERROR,
      payload: error,
    }),
    "Couldn't export quote summary to Excel",
  ),
};

export const refetchQuoteItemHeaders = {
  request: (payload: { quoteId: string; quoteItemIds?: string[] }) => ({
    type: types.REFETCH_QUOTE_ITEM_HEADERS_REQUEST,
    payload,
  }),
  success: () => ({
    type: types.REFETCH_QUOTE_ITEM_HEADERS_SUCCESS,
  }),
  error: (payload: { error: unknown }) => ({
    type: types.REFETCH_QUOTE_ITEM_HEADERS_ERROR,
    payload,
  }),
};
